import type {
	CAInputMessage,
	CALabelAppearance,
	CALink,
	CASelectGroup,
	CASelectInputType,
	CASelectOption,
	CASelectType,
} from '@ds/interfaces'
import { defineComponent, type PropType } from 'vue'

/**
 * The `CASelectDropdown` component is a versatile select dropdown component that can be used for
 * various purposes, including simple selects, advanced options, grouped options, and navigation.
 * @component CASelectDropdown
 *
 * @prop {string} [additionalClass=''] - Additional classes to be added to the select wrapper
 * @prop {CASelectOption[]} [advancedOptions=[]] - Advanced options for the select dropdown
 * @prop {string} [dataTest=''] - Data attribute for testing
 * @prop {boolean} [disabled=false] - Whether the select dropdown is disabled
 * @prop {CASelectGroup[]} [groupOptions=[]] - Grouped options for the select dropdown
 * @prop {boolean} [hideLabel=false] - Whether the label should be hidden
 * @prop {string} id - ID for the select dropdown (required)
 * @prop {string} [label=''] - Label of the select dropdown
 * @prop {CALabelAppearance} [labelAppearance=''] - Appearance of the label
 * @prop {CAInputMessage | null} [message=null] - Message to display below the select dropdown
 * @prop {CASelectInputType} [modelValue=null] - The model value for the select dropdown
 * @prop {boolean} [multiple=false] - Whether the select dropdown allows multiple selections
 * @prop {CALink[]} [navigationOptions=[]] - Navigation options for the select dropdown
 * @prop {CASelectOption[]} [options=[]] - Options for the select dropdown
 * @prop {boolean} [required=false] - Whether the select dropdown is required
 * @prop {string} [screenReaderLabel=''] - Screen reader label for the select dropdown
 * @prop {string} [tooltip=''] - Tooltip for the select dropdown
 * @prop {CASelectType} [type='simple'] - The type of select dropdown
 *
 * @emit update:modelValue - When the selected value changes.
 * @emit message - When a message is emitted, such as an error or info message
 *
 * @slot default - The default slot for custom options.
 *
 * @typedef {('simple' | 'advanced' | 'grouped' | 'navigation')} CASelectType - The type of select dropdown.
*/
export default defineComponent({
	name: 'CASelectDropdown',

	props: {
		/**
		 * Additional classes to be added to the select wrapper.
		 * @default ''
		 * @type {string}
		 */
		additionalClass: { default: '', type: String },
		/**
		 * Advanced options for the select dropdown.
		 * @default null
		 * @type {CASelectOption[]}
		 */
		advancedOptions: { default: [], type: Array as PropType<CASelectOption[]> },
		/**
		 * Data attribute for testing.
		 * @default ''
		 * @type {string}
		 */
		dataTest: { default: '', type: String },
		/**
		 * Whether the select dropdown is disabled.
		 * @default false
		 * @type {boolean}
		 */
		disabled: { default: false, type: Boolean },
		/**
		 * Grouped options for the select dropdown.
		 * @default []
		 * @type {CASelectGroup[]}
		 */
		groupOptions: { default: [], type: Array as PropType<CASelectGroup[]> },
		/**
		 * Whether the label should be hidden.
		 * @default false
		 * @type {boolean}
		 */
		hideLabel: { default: false, type: Boolean },
		/**
		 * ID for the select dropdown.
		 * @required
		 * @type {string}
		 */
		id: { required: true, type: String },
		/**
		 * Label of the select dropdown.
		 * @default ''
		 * @type {string}
		 */
		label: { default: '', type: String },
		/**
		 * Appearance of the label.
		 * @default 'hidden'
		 * @type {CALabelAppearance}
		 * @enum {hidden | static | grid}
		 */
		labelAppearance: { default: '', type: String as PropType<CALabelAppearance> },
		/**
		 * Message to display below the select dropdown.
		 * @default null
		 * @type {CAInputMessage | null}
		 */
		message: { default: null, type: Object as PropType<CAInputMessage | null> },
		/**
		 * The model value for the select dropdown.
		 * @default null
		 * @type {CASelectInputType}
		 * @enum {string | number | null}
		 */
		modelValue: {
			default: null,
			type: [
				Number,
				String,
			] as PropType<CASelectInputType>,
		},
		/**
		 * Whether the select dropdown allows multiple selections.
		 * @default false
		 * @type {boolean}
		 */
		multiple: { default: false, type: Boolean },
		/**
		 * Navigation options for the select dropdown.
		 * @default null
		 * @type {CALink[]}
		 */
		navigationOptions: { default: [], type: Array as PropType<CALink[]> },
		/**
		 * Options for the select dropdown.
		 * @default []
		 * @type {CASelectOption[]}
		 */
		options: { default: [], type: Array as PropType<CASelectOption[]> },
		/**
		 * Whether the select dropdown is required.
		 * @default false
		 * @type {boolean}
		 */
		required: { default: false, type: Boolean },
		/**
		 * Screen reader label for the select dropdown.
		 * @default ''
		 * @type {string}
		 */
		screenReaderLabel: { default: '', type: String },
		/**
		 * Tooltip for the select dropdown.
		 * @default ''
		 * @type {string}
		 */
		tooltip: { default: '', type: String },
		/**
		 * The type of select dropdown.
		 * @default 'simple'
		 * @type {CASelectType}
		 * @enum {simple | advanced | grouped | navigation}
		 */
		type: { default: 'simple', type: String as PropType<CASelectType> },
	},

	emits: {
		'update:modelValue': (_value: CASelectInputType) => true,
		message: (_message?: CAInputMessage) => true,
	},

	data(): Data {
		return { isFocused: false }
	},

	computed: {
		internalValue: {
			get() {
				return this.modelValue
			},
			set(value: CASelectInputType) {
				this.$emit('update:modelValue', value)
			},
		},

		labelClasses(): string {
			return `ca-select__label ${this.labelAppearance ? `ca-select__label--${this.labelAppearance}` : ''}`
		},

		selectOptions(): CALink[] | CASelectGroup[] | CASelectOption[] {
			switch (this.type) {
				case 'advanced':
					return this.advancedOptions || []
				case 'grouped':
					return this.groupOptions || []
				case 'navigation':
					return this.navigationOptions || []
				case 'simple':
					return this.options || []
				default:
					return []
			}
		},

		selectWrapperClasses(): string {
			return [
				'ca-form-control',
				this.required ? 'ca-form-control__required' : '',
				'ca-select',
				`ca-select--${this.type}`,
				this.labelAppearance ? `ca-select__${this.labelAppearance}` : '',
				this.disabled ? 'ca-select--disabled' : '',
				this.isFocused ? 'ca-select--focused' : '',
				this.message?.type === 'error' ? 'ca-select__error' : '',
				this.required ? 'ca-select--required' : '',
				this.tooltip ? 'ca-select--tooltip' : '',
				this.additionalClass || '',
			].filter(Boolean).join(' ')
		},

		showRequired(): boolean {
			return this.required && (!!this.label && !this.hideLabel)
		},
	},

	mounted() {
		document.addEventListener('DOMContentLoaded', () => {
			const select = document.querySelector('select') as HTMLSelectElement
			const selectWrapper = document.querySelector('.ca-select') as HTMLDivElement

			if (select) {
				select.addEventListener('focus', () => {
					selectWrapper.classList.add('ca-select--focus')
				})

				select.addEventListener('blur', () => {
					selectWrapper.classList.remove('ca-select--focus')
				})
			}
		})
	},

	render() {
		return (
			<div
				class={this.selectWrapperClasses}
				data-test="ca-select"
				v-tooltip={this.tooltip}
			>
				{this.label && !this.hideLabel &&
					<label
						class={this.labelClasses}
						data-test="ca-select-label"
						for={this.id}
					>
						{this.label} {this.showRequired && <span class="ca-required-text">*</span>}
					</label>
				}

				{this.screenReaderLabel &&
					<label
						class={`${this.labelClasses} sr-only`}
						data-test="ca-select-label"
						for={this.id}
					>
						{this.screenReaderLabel} {this.showRequired && <span class="ca-required-text">*</span>}
					</label>
				}

				<div class={`${this.labelAppearance ? `ca-select__select--${this.labelAppearance}` : ''}`}>
					{this.type === 'simple' &&
						<select
							aria-describedby={`${this.id}-message`}
							aria-invalid={!!this.message && this.message.type === 'error'}
							aria-labelledby={`${this.id}-label`}
							aria-required={this.required}
							class="ca-select__select"
							data-test={this.dataTest}
							data-test-type={`ca-select-type-${this.type}`}
							disabled={this.disabled}
							id={this.id}
							multiple={this.multiple}
							name={this.id}
							required={this.required}
							value={this.internalValue}
							onInput={e => {
								this.internalValue = (e.target as HTMLSelectElement).value as CASelectInputType
							}}
						>
							{this.$slots.default?.()}

							{this.options?.map(option =>
								<option
									key={option.id}
									disabled={option.disabled}
									value={option.id}
								>
									{option.label}
								</option>
							)}
						</select>
					}

					{this.type === 'advanced' &&
						<select
							aria-describedby={`${this.id}-message`}
							aria-invalid={!!this.message && this.message.type === 'error'}
							aria-labelledby={`${this.id}-label`}
							class="ca-select__select--advanced"
							data-test={this.dataTest}
							data-test-type={`ca-select-type-${this.type}`}
							disabled={this.disabled}
							id={this.id}
							multiple={this.multiple}
							name={this.id}
							required={this.required}
							value={this.internalValue}
							onInput={e => {
								this.internalValue = (e.target as HTMLSelectElement).value as CASelectInputType
							}}
						>
							{this.$slots.default?.()}

							{this.advancedOptions?.map(option =>
								<option
									key={option.id}
									disabled={option.disabled}
									value={option.id}
								>
									{option.label}
								</option>
							)}
						</select>
					}

					{this.type === 'navigation' &&
						<select
							aria-describedby={`${this.id}-message`}
							aria-invalid={!!this.message && this.message.type === 'error'}
							aria-labelledby={`${this.id}-label`}
							class="ca-select__select--navigation"
							data-test={this.dataTest}
							data-test-type={`ca-select-type-${this.type}`}
							disabled={this.disabled}
							id={this.id}
							multiple={this.multiple}
							name={this.id}
							required={this.required}
							role="tablist"
							value={this.internalValue}
							onInput={e => {
								this.internalValue = (e.target as HTMLSelectElement).value as CASelectInputType
							}}
						>
							{this.$slots.default?.()}

							{this.navigationOptions?.map((option, index) =>
								<option
									key={`${this.id}-${index}`}
									disabled={option.disabled}
									role="tab"
									selected={`${index}` === String(this.internalValue)}
									value={index}
								>
									{option.label}
								</option>
							)}
						</select>
					}

					{this.type === 'grouped' &&
						<select
							aria-describedby={`${this.id}-message`}
							aria-invalid={!!this.message && this.message.type === 'error'}
							aria-labelledby={`${this.id}-label`}
							class="ca-select__select ca-select__select--grouped"
							data-test={this.dataTest}
							data-test-type={`ca-select-type-${this.type}`}
							disabled={this.disabled}
							id={this.id}
							multiple={this.multiple}
							name={this.id}
							required={this.required}
							value={this.internalValue}
							onInput={e => {
								this.internalValue = (e.target as HTMLSelectElement).value as CASelectInputType
							}}
						>
							{this.$slots.default?.()}

							{this.groupOptions?.map((group, index) =>
								<optgroup
									key={index}
									label={group.groupLabel}
								>
									{group.options.map(option =>
										<option
											key={`${group}-${option.id}`}
											value={option.id}
										>
											{option.label}
										</option>
									)}
								</optgroup>
							)}
						</select>
					}
				</div>

				{this.message && this.message.text &&
					<p
						class={`ca-select__message ca-select__message--${this.message.type}`}
						data-test="ca-select-message"
						id={`${this.id}-message`}
						innerHTML={this.message.text}
						role={this.message.type === 'error' ? 'alert' : 'status'}
					/>
				}
			</div>
		)
	},
})

interface Data {
	isFocused: boolean
}
